import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/apiClient";

const INTERESTS = [
  "AI CRM",
  "School Management",
  "Non-Profit AI CRM",
  "Mobile Apps",
  "AI Development",
  "AI Research",
  "Technology & AI Innovation",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Transmission received. We'll be in touch within 24h.");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section relative bg-[#FFFFFF]" data-testid="contact-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">[03] / Contact</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-10 text-[#0A0A0A]">
              Let's build something
              <br />
              <span className="text-[#C99B1A] italic font-light">brilliant.</span>
            </h2>

            <div className="space-y-8 text-black/65">
              <div>
                <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                  Email
                </div>
                <a
                  href="mailto:hello@barraq.ai"
                  className="text-2xl text-[#0A0A0A] hover:text-[#C99B1A] transition-colors font-display"
                  data-testid="contact-email"
                >
                  hello@barraq.ai
                </a>
              </div>
              <div>
                <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                  Reach
                </div>
                <p className="text-base leading-relaxed">
                  US · Worldwide.
                  <br />
                  We respond within 24 hours, in any timezone.
                </p>
              </div>
              <div>
                <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                  We are good for
                </div>
                <p className="text-base leading-relaxed">
                  Ambitious teams, government &amp; enterprise pilots, foundations chasing measurable impact.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-12">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[#C99B1A]/40 bg-[#C99B1A]/5 p-10 text-center"
                data-testid="contact-success"
              >
                <div className="w-14 h-14 rounded-full bg-[#C99B1A] mx-auto flex items-center justify-center mb-6">
                  <Check size={26} strokeWidth={3} className="text-white" />
                </div>
                <h3 className="font-display text-3xl mb-3 text-[#0A0A0A]">Transmission received.</h3>
                <p className="text-black/65 mb-6">
                  We'll be in touch within 24 hours. In the meantime, watch your inbox.
                </p>
                <button onClick={() => setDone(false)} className="btn-secondary">
                  Send another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                      Your name
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="First, Last Name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                      Email
                    </label>
                    <input
                      type="email"
                      className="field-input"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                    Company / Organization
                  </label>
                  <input
                    type="text"
                    className="field-input"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Optional"
                    data-testid="contact-company-input"
                  />
                </div>

                <div>
                  <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                    Interested in
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => setForm({ ...form, interest: i })}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all ${
                          form.interest === i
                            ? "border-[#C99B1A] bg-[#C99B1A]/10 text-[#C99B1A]"
                            : "border-black/15 text-black/65 hover:border-black/40 hover:text-[#0A0A0A]"
                        }`}
                        data-testid={`interest-${i.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                    Tell us what you're building
                  </label>
                  <textarea
                    className="field-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Goals, current stack, timelines..."
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {loading ? "Transmitting..." : "Send Transmission"}
                  <ArrowUpRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
